import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, CheckCircle2, ClipboardList, Loader2, Store, X,
} from 'lucide-react';
import EscolherAnexo from '../../EscolherAnexo';

// =====================================================================
// FINANCEIRO — Cofrinho: o FORMULÁRIO da loja.
//
// A loja registra a arrecadação guardada no cofre: a data, o número da banana
// (o envelope) e o valor; o comprovante é OPCIONAL (foto OU PDF). A competência
// é derivada pelo corte do dia 25 e mostrada ao vivo, para não haver surpresa
// sobre em que mês o lançamento cai.
//
// ⚠️ A tela NÃO reimplementa regra de perfil: quem diz se a pessoa registra e se
//    escolhe a loja é o `GET /opcoes`.
// ⚠️ A LOJA do gerente NÃO é enviada: o servidor a lê do cadastro e ignora o
//    corpo (é ele quem decide de quem é o lançamento).
// =====================================================================

const API = '/api/financeiro/cofrinho';
// Paleta do módulo FINANCEIRO (verde-petróleo). Tela dentro de um módulo usa a
// cor DELE (docs/ARQUITETURA.md › Paleta dos módulos).
const COR = '#0d7a74';
const COR_FORTE = '#115e59';
const COR_FUNDO = '#f0fdfa';
const COR_BORDA = '#99f6e4';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const brl = (n) => Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const mesBr = (comp) => {
  if (!/^\d{4}-\d{2}$/.test(String(comp || ''))) return '—';
  const [a, m] = comp.split('-');
  const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${nomes[Number(m) - 1]}/${a}`;
};

const usuarioLogado = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};

/**
 * A competência pelo corte do dia 25 — PRÉVIA para a tela.
 *
 * ⚠️ Espelho da coluna gerada `COMPETENCIA` do banco e da regra pura
 *    `competenciaCofrinho` (server/financeiro/cofrinho/cofrinho-regras.js). A
 *    VERDADE do que fica gravado é a coluna gerada (o banco não diverge de si
 *    mesmo); isto aqui só mostra ao usuário onde a data cai antes de enviar.
 */
const competenciaDe = (dataIso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dataIso || ''))) return null;
  const [a, m, d] = dataIso.split('-').map(Number);
  const p2 = (n) => String(n).padStart(2, '0');
  if (d <= 25) return `${a}-${p2(m)}`;
  return m === 12 ? `${a + 1}-01` : `${a}-${p2(m + 1)}`;
};

const Campo = ({ label, obrigatorio, dica, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <label style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
      {label} {obrigatorio && <span style={{ color: '#dc2626' }}>*</span>}
    </label>
    {children}
    {dica && <span style={{ fontSize: 12, color: '#94a3b8' }}>{dica}</span>}
  </div>
);

const estiloInput = {
  padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8,
  fontSize: 15, color: '#0f172a', background: '#fff', width: '100%',
  boxSizing: 'border-box', fontFamily: 'inherit',
};

const CofrinhoRegistro = ({ onIrParaConsulta }) => {
  const [opcoes, setOpcoes] = useState(null);
  const [lojas, setLojas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [gravado, setGravado] = useState(null);

  const [form, setForm] = useState({ data: '', loja: '', banana: '', valor: '' });
  const [comprovante, setComprovante] = useState(null);

  const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const rOpcoes = await fetch(`${API}/opcoes`, { headers: auth() });
        if (!rOpcoes.ok) throw new Error('opcoes');
        const o = await rOpcoes.json();
        if (!vivo) return;
        setOpcoes(o);

        if (o.escolheLoja) {
          const rLojas = await fetch(`${API}/lojas`, { headers: auth() });
          if (vivo && rLojas.ok) setLojas(await rLojas.json());
        }

        setForm((f) => ({
          ...f,
          data: o.hoje || '',
          loja: o.escolheLoja ? '' : String(usuarioLogado()?.loja || '').trim(),
        }));
      } catch {
        if (vivo) setErro('Não foi possível carregar o formulário. Recarregue a página.');
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  const enviar = async (e) => {
    e.preventDefault();
    setErro('');
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('data', form.data);
      if (opcoes?.escolheLoja) fd.append('loja', form.loja);
      fd.append('banana', form.banana);
      fd.append('valor', form.valor);
      if (comprovante) fd.append('comprovante', comprovante);

      const r = await fetch(API, { method: 'POST', headers: auth(), body: fd });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.message || 'Não foi possível registrar o lançamento.');
      setGravado(corpo);
    } catch (ex) {
      setErro(ex.message);
    } finally {
      setEnviando(false);
    }
  };

  const novoRegistro = () => {
    setGravado(null);
    setComprovante(null);
    // Preserva data e loja (quem lança vários lançamentos do mesmo dia não reescolhe);
    // banana e valor zeram — repeti-los produziria registro duplicado sem notar.
    setForm((f) => ({ ...f, banana: '', valor: '' }));
  };

  if (carregando) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#64748b', padding: 24 }}>
        <Loader2 size={18} className="spin" /> Carregando o formulário…
      </div>
    );
  }

  if (!opcoes?.podeRegistrar) {
    return (
      <div style={{ padding: 24, background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
        Seu perfil consulta o cofrinho, mas não registra lançamentos.
      </div>
    );
  }

  // --- confirmação ---------------------------------------------------
  if (gravado) {
    const linha = (rotulo, valor) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
        <span style={{ color: '#64748b', fontSize: 14 }}>{rotulo}</span>
        <span style={{ color: '#0f172a', fontSize: 14, fontWeight: 700, textAlign: 'right' }}>{valor}</span>
      </div>
    );
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ display: 'inline-flex', padding: 16, background: '#dcfce7', borderRadius: '50%', marginBottom: 12 }}>
            <CheckCircle2 size={36} color="#16a34a" />
          </div>
          <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Lançamento registrado</h3>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14 }}>
            Registro nº <strong style={{ color: COR_FORTE }}>{gravado.id}</strong> — o Financeiro já enxerga este lançamento.
          </p>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          {linha('Loja', gravado.loja)}
          {linha('Data', (gravado.data || '').split('-').reverse().join('/'))}
          {linha('Competência', mesBr(gravado.competencia))}
          {linha('Banana', gravado.banana)}
          {linha('Valor', <span style={{ fontVariantNumeric: 'tabular-nums' }}>{brl(gravado.valor)}</span>)}
          {linha('Comprovante', gravado.comprovanteUrl ? 'Anexado' : 'Sem comprovante')}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button onClick={novoRegistro} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 20px',
            background: COR, color: '#fff', border: 'none', borderRadius: 8,
            fontWeight: 700, fontSize: 15, cursor: 'pointer',
          }}>
            <ClipboardList size={18} /> Registrar outro lançamento
          </button>
          {onIrParaConsulta && (
            <button onClick={onIrParaConsulta} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 20px',
              background: COR_FUNDO, color: COR_FORTE, border: `1px solid ${COR_BORDA}`,
              borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: 'pointer',
            }}>
              Ver o cofrinho
            </button>
          )}
        </div>
      </div>
    );
  }

  // --- formulário ----------------------------------------------------
  const competenciaPrevia = competenciaDe(form.data);
  return (
    <form onSubmit={enviar} style={{ maxWidth: 720, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Registro do Cofrinho</h3>
        <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14, lineHeight: 1.5 }}>
          Registre a arrecadação guardada no cofre da loja. O Financeiro consolida por competência
          e repassa ao Instituto Ronald. O registro é definitivo — correção é pedida ao Financeiro.
        </p>
      </div>

      {erro && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: 14, marginBottom: 20,
          background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 14,
        }}>
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} /> <span>{erro}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18, marginBottom: 18 }}>
        <Campo label="Data do registro" obrigatorio
          dica={competenciaPrevia ? `Competência: ${mesBr(competenciaPrevia)} (corte no dia 25)` : 'Corte no dia 25 do mês.'}>
          {/* `max` = hoje: data no futuro é recusada pelo servidor; barrar aqui evita a viagem. */}
          <input type="date" required value={form.data} max={opcoes.hoje}
            onChange={(e) => set('data', e.target.value)} style={estiloInput} />
        </Campo>

        <Campo label="Loja" obrigatorio>
          {opcoes.escolheLoja ? (
            <select required value={form.loja} onChange={(e) => set('loja', e.target.value)} style={estiloInput}>
              <option value="">Escolha a loja…</option>
              {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          ) : (
            <div style={{
              ...estiloInput, display: 'flex', alignItems: 'center', gap: 8,
              background: '#f8fafc', color: '#334155', fontWeight: 700,
            }}>
              <Store size={16} color={COR} /> {form.loja || '—'}
            </div>
          )}
        </Campo>

        <Campo label="Número da banana" obrigatorio dica="O número do envelope (malote).">
          <input required value={form.banana} onChange={(e) => set('banana', e.target.value)}
            placeholder="Ex.: 123456" maxLength={60} style={estiloInput} />
        </Campo>

        <Campo label="Valor" obrigatorio
          dica={form.valor ? brl(String(form.valor).replace(/\./g, '').replace(',', '.')) : 'Use vírgula para os centavos.'}>
          <input required value={form.valor} onChange={(e) => set('valor', e.target.value)}
            placeholder="150,00" inputMode="decimal"
            style={{ ...estiloInput, fontVariantNumeric: 'tabular-nums' }} />
        </Campo>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Campo label="Foto da Banana - Evidência" dica="Opcional — foto OU PDF. Máximo 12 MB.">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <EscolherAnexo
              cor={COR}
              nome={comprovante?.name || ''}
              rotulo="Anexar evidência"
              onArquivo={(arq) => setComprovante(arq || null)}
            />
            {comprovante && (
              <button type="button" onClick={() => setComprovante(null)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6,
                fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}>
                <X size={14} /> Remover
              </button>
            )}
          </div>
        </Campo>
      </div>

      <button type="submit" disabled={enviando} style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        width: '100%', padding: '14px 24px', background: enviando ? '#cbd5e1' : COR,
        color: '#fff', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 16,
        cursor: enviando ? 'default' : 'pointer',
      }}>
        {enviando ? <><Loader2 size={18} className="spin" /> Registrando…</> : <>Registrar lançamento</>}
      </button>
    </form>
  );
};

export default CofrinhoRegistro;
